/**
 * @module modules/usePrevious
 * @description React hook for tracking the previous value of a prop or state.
 * Useful for comparing current and previous values in effects or render logic.
 */

import { useEffect, useRef } from 'react';

/**
 * @function usePrevious
 * @template T
 * @description React hook that stores and returns the previous value from the last render.
 * Updates the stored value after each render, so the returned value is always one render behind.
 * Useful for comparing current vs previous values in effects or conditional rendering.
 * @param {T} value - Current value to track
 * @returns {T | undefined} Previous value from last render, or undefined on first render
 */
export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};
